import React, { Fragment, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FiX, FiFileText, FiCalendar, FiList, FiInfo, FiActivity, FiFolder } from 'react-icons/fi';

const TaskViewModal = ({ isOpen, onClose, task }) => {
  const modalRef = useRef(null);
  
  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (!task) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-90 scale-95"
            >
              <div 
                ref={modalRef}
                className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all"
              >
                {/* Header with Orange Theme */}
                <div className="bg-gradient-to-r from-orange-300 to-orange-400 p-4 sm:p-6 border-b border-orange-500">
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                        <FiActivity className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold text-black">
                          Task Details
                        </h2>
                        <div className="flex items-center mt-1 text-black text-sm">
                          <FiFolder className="w-3 h-3 mr-1" />
                          <span>Programs Department</span>
                        </div>
                      </div>
                    </div>
                    <button 
                      onClick={onClose} 
                      className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                    >
                      <FiX size={20} />
                    </button>
                  </div>
                </div>

                {/* Content Sections */}
                <div className="p-4 sm:p-6 space-y-6">
                  {/* Task Details Section */}
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="text-lg font-bold text-gray-800 flex items-center mb-4">
                      <FiFileText className="mr-2 text-orange-500" /> Task Details
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600">
                      <p><strong>Title:</strong> {task.title}</p>
                      <p><strong>Priority:</strong> {task.priority}</p>
                      <p><strong>Status:</strong> {task.status ? task.status.replace(/_/g, ' ').toUpperCase() : 'N/A'}</p>
                      <p><strong>Start Date:</strong> {task.startDate ? new Date(task.startDate).toLocaleDateString() : 'N/A'}</p>
                      <p><strong>Due Date:</strong> {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'N/A'}</p>
                      <p className="col-span-1 sm:col-span-2"><strong>Description:</strong> {task.description || 'No description available.'}</p>
                    </div>
                  </div>

                  {/* Activity Details Section */}
                  <div className="p-4 border border-gray-200 rounded-lg">
                    <h4 className="text-lg font-bold text-gray-800 flex items-center mb-4">
                      <FiList className="mr-2 text-orange-500" /> Activity Details
                    </h4>
                    <div className="text-sm text-gray-600">
                      <p><strong>Activity Name:</strong> {task.activityName}</p>
                      {task.activity && (
                          <p><strong>Activity Status:</strong> {task.activity.status}</p>
                      )}
                    </div>
                  </div>

                  {/* Project Details Section (Conditional) */}
                  {task.project && (
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <h4 className="text-lg font-bold text-gray-800 flex items-center mb-4">
                        <FiInfo className="mr-2 text-orange-500" /> Project Details
                      </h4>
                      <div className="text-sm text-gray-600">
                        <p><strong>Project Name:</strong> {task.project.name}</p>
                        <p><strong>Project Manager:</strong> {task.project.manager}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Close Button */}
                <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-200">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TaskViewModal;